import React, { useEffect, useRef, useState } from 'react';
import { Edit, MoreVertical, Trash2 } from 'lucide-react';
import './RepositoryRow.css';
import Tooltip from '../../components/common/Tooltip/Tooltip';

const ROW_TRUNCATE_LIMIT = 50;

const truncate = (str, max = ROW_TRUNCATE_LIMIT) =>
    str && str.length > max ? str.slice(0, max) + '…' : str;

const RepositoryRow = ({ item, onEdit, onDelete }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpen]);

    const handleMenuAction = (action) => {
        setMenuOpen(false);
        if (action === 'edit' && onEdit) onEdit(item);
        if (action === 'delete' && onDelete) onDelete(item.id);
    };

    const getAvatarGradient = () => {
        return 'linear-gradient(to bottom right, #3b82f6, #a855f7)';
    };

    const getInitials = (name) => {
        if (!name) return 'RE';
        const words = name.split(' ').filter(word => word.length > 0);
        if (words.length >= 2) {
            return (words[0][0] + words[1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const initials = getInitials(item.name);
    // Support both API field names
    const ownerName = item.github_username || item.owner || item.username || '';
    const repoPath = item.repository_full_name || item.repository || '';
    const branch = item.branch || '';

    return (
        <div className="connection-row">
            <div className="connection-main">
                <div
                    className="connection-avatar"
                    style={{ background: getAvatarGradient() }}
                >
                    {initials}
                </div>
                <div className="connection-details">
                    <h4 className="connection-name">{item.name}</h4>
                    <p className="connection-provider">{ownerName}</p>
                    <div className="connection-meta repo-meta">
                        {repoPath && (
                            <span className="repo-meta-label">
                                Repository:{' '}
                                {repoPath.length > ROW_TRUNCATE_LIMIT ? (
                                    <Tooltip text={repoPath} className="repo-details">
                                        <span className="repo-meta-value">{truncate(repoPath)}</span>
                                    </Tooltip>
                                ) : (
                                    <span className="repo-meta-value">{repoPath}</span>
                                )}
                            </span>
                        )}
                        {branch && (
                            <span className="repo-meta-label">
                                Branch:{' '}
                                {branch.length > ROW_TRUNCATE_LIMIT ? (
                                    <Tooltip text={branch} className="repo-details">
                                        <span className="repo-meta-value">{truncate(branch)}</span>
                                    </Tooltip>
                                ) : (
                                    <span className="repo-meta-value">{branch}</span>
                                )}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="connection-actions">
                <div className="more-menu-wrapper" ref={menuRef}>
                    <button
                        className="more-menu-btn"
                        onClick={() => setMenuOpen((prev) => !prev)}
                        aria-label="More options"
                    >
                        <MoreVertical style={{ width: "1.25vw", height: "1.25vw" }} />
                    </button>

                    {menuOpen && (
                        <div className="more-menu-dropdown">
                            <button
                                className="more-menu-item"
                                onClick={() => handleMenuAction("edit")}
                            >
                                <Edit style={{ width: "0.833vw", height: "0.833vw" }} />
                                <span>Edit</span>
                            </button>
                            <button
                                className="more-menu-item more-menu-item--delete"
                                onClick={() => handleMenuAction("delete")}
                            >
                                <Trash2 style={{ width: "0.833vw", height: "0.833vw" }} />
                                <span>Delete</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RepositoryRow;